import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { EditorHeader } from "./EditorHeader";
import { EditorForm } from "./shared/EditorForm";
import { EditorField } from "./shared/EditorField";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const DownloadableFilesEditor = () => {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ['downloadable_file_categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('downloadable_file_categories')
        .select('*')
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      return data;
    }
  });

  const { data: files, isLoading: filesLoading } = useQuery({
    queryKey: ['downloadable_files'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('downloadable_files')
        .select('*')
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      return data;
    }
  });

  const addFileMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      if (!selectedFile) {
        throw new Error('No file selected');
      }

      // Upload file to Supabase Storage
      const fileExt = selectedFile.name.split('.').pop();
      const filePath = `${crypto.randomUUID()}.${fileExt}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('legal_documents')
        .upload(filePath, selectedFile);

      if (uploadError) throw uploadError;

      // Get the public URL
      const { data: publicUrlData } = supabase.storage
        .from('legal_documents')
        .getPublicUrl(filePath);

      const newFile = {
        title: String(formData.get('title')),
        file_url: publicUrlData.publicUrl,
        category: String(formData.get('category')),
        description: formData.get('description') ? String(formData.get('description')) : null,
      };

      const { error } = await supabase
        .from('downloadable_files')
        .insert([newFile]);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['downloadable_files'] });
      toast.success('File added successfully');
      setSelectedFile(null);
    },
    onError: (error) => {
      toast.error('Failed to add file: ' + error.message);
    }
  });

  const deleteFileMutation = useMutation({
    mutationFn: async (id: string) => {
      // First get the file URL to extract the path
      const { data: fileData, error: fetchError } = await supabase
        .from('downloadable_files')
        .select('file_url')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;

      // Extract file path from URL
      const filePath = fileData.file_url.split('/').pop();

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('legal_documents')
        .remove([filePath]);

      if (storageError) throw storageError;

      // Delete from database
      const { error } = await supabase
        .from('downloadable_files')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['downloadable_files'] });
      toast.success('File deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete file: ' + error.message);
    }
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await addFileMutation.mutateAsync(new FormData(e.currentTarget));
      (e.target as HTMLFormElement).reset();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  if (categoriesLoading || filesLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <EditorHeader 
        title="Downloadable Files" 
        description="Manage downloadable files in the data room" 
      />

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Add New File</h3>
        <EditorForm onSubmit={handleSubmit} isSubmitting={isSubmitting}>
          <EditorField
            label="Title"
            name="title"
            required
          />
          <div className="space-y-2">
            <Label className="text-sm font-medium">File</Label>
            <Input
              type="file"
              onChange={handleFileChange}
              className="cursor-pointer"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Category</label>
            <select
              name="category"
              className="w-full rounded-md border border-input bg-background px-3 py-2"
              required
            >
              {categories?.map((category) => (
                <option key={category.id} value={category.category}>
                  {category.title}
                </option>
              ))}
            </select>
          </div>
          <EditorField
            label="Description"
            name="description"
            type="textarea"
          />
          <Button type="submit" className="w-full">
            <Upload className="w-4 h-4 mr-2" />
            Upload File
          </Button>
        </EditorForm>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Current Files</h3>
        <div className="space-y-4">
          {files?.map((file) => (
            <div
              key={file.id}
              className="flex items-center justify-between p-4 rounded-lg border"
            >
              <div>
                <h4 className="font-medium">{file.title}</h4>
                <p className="text-sm text-muted-foreground">
                  Category: {categories?.find(c => c.category === file.category)?.title}
                </p>
              </div>
              <Button
                variant="destructive"
                size="icon"
                onClick={() => deleteFileMutation.mutate(file.id)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};